import {
  artifactVersionSchema,
  calendarDateSchema,
  canonicalJsonStringify,
  canonicalRouteTemplateWorkType,
  instantSchema,
  locationSchema,
  normalizeRouteSeedForCurrentScoring,
  routeDraftEndpointLocationSnapshot,
  routeDraftSnapshotSchema,
  ROUTE_DRAFT_WIRE_WRITE_MODE,
  routeCopySourceSnapshotSha256,
  routeFingerprintSha256,
  routeTemplateSchema,
  routeTemplateSourceSnapshotSchema,
  routeVariantSchema,
  ROUTE_SCORING_VERSION,
  sha256HexUtf8,
  shiftAggregateSchema,
  shiftEditorDraftDistanceForConfirmation,
  shiftEditorDraftConfirmedEndpointSchema,
  shiftEditorDraftEndpointSchema,
  shiftEditorDraftSchema,
  uuidSchema,
  vehicleSchema,
  type ArtifactVersion,
  type JsonValue,
  type Location,
  type LocationSnapshot,
  type RouteSeed,
  type RouteSnapshot,
  type RouteTemplate,
  type RouteTemplateSourceSnapshot,
  type RouteVariant,
  type RouteOfferSource,
  type ShiftAggregate,
  type ShiftEditorDraft,
  type ShiftEditorDraftConfirmedEndpoint,
  type ShiftEditorDraftEndpoint,
  type TripSegment,
  type Vehicle,
  type VehicleSnapshot,
} from '@der-maschinfuehrer/contracts';

import { WEB_CATALOG } from '../catalog/generated-catalog';
import type { AppStorage } from '../storage/app-storage';

type ShiftMutation = Parameters<AppStorage['saveShift']>[0];
type ShiftDraftBundleMutation = Parameters<AppStorage['commitShiftDraftBundle']>[0]['mutation'];
type UserDefinedLocationMutation = ShiftDraftBundleMutation['user_defined_locations'][number];

export const ALPHA_MANUAL_ROUTE_SCORING_VERSION = ROUTE_SCORING_VERSION;
export const ACTIVE_CATALOG_VERSION: ArtifactVersion = artifactVersionSchema.parse({
  version: WEB_CATALOG.formatVersion,
  sha256: WEB_CATALOG.catalogVersion.replace(/^sha256-/u, ''),
});

export type AlphaUuidFactory = () => string;
export type DraftEndpointName = 'origin' | 'destination' | 'planned_destination';

export interface DraftRouteMutationOptions {
  readonly persistedRouteVariantId: string | null;
}

export interface ApplyDraftRouteSeedInput {
  readonly route_variant_id: string;
  readonly route_selection: Extract<
    NonNullable<ShiftEditorDraft['route_selection']>,
    { readonly kind: 'offer' }
  >;
  readonly origin: LocationSnapshot;
  readonly via: readonly LocationSnapshot[];
  readonly destination: LocationSnapshot;
  readonly route_seed: RouteSeed;
  readonly route_copy_source: ShiftEditorDraft['route_copy_source'];
}

export function routeOfferSelectionId(source: RouteOfferSource, identity: string): string {
  const value = `${source}:${identity}`;
  if (value.length > 256) throw new RangeError('route offer selection ID is too long');
  return value;
}

export interface DraftEndpointUpdateValue {
  readonly name: string;
  readonly country_code: 'CZ' | 'DE';
  readonly key_identifier?: ShiftEditorDraftEndpoint['key_identifier'];
  readonly carried_location_snapshot?: LocationSnapshot;
  readonly catalog_document_id?: string;
  readonly catalog_version?: ArtifactVersion;
}

export interface ShiftDraftValidationIssue {
  readonly field: DraftEndpointName | 'route_confirmation' | 'partial' | 'vehicles' | 'distance';
  readonly message: string;
}

export type ShiftDraftValidationResult =
  | { readonly valid: true; readonly issues: readonly [] }
  | { readonly valid: false; readonly issues: readonly ShiftDraftValidationIssue[] };

export class AlphaShiftDraftValidationError extends Error {
  public readonly issues: readonly ShiftDraftValidationIssue[];

  public constructor(issues: readonly ShiftDraftValidationIssue[]) {
    super(issues.map((issue) => issue.message).join(' '));
    this.name = new.target.name;
    this.issues = issues;
  }
}

export function localCalendarDate(date: Date = new Date()): string {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('service date requires a valid local Date');
  }
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return calendarDateSchema.parse(`${year}-${month}-${day}`);
}

export function createEmptyOpenShiftMutation(input: {
  readonly datasetEpoch: string;
  readonly shiftId: string;
  readonly serviceDate?: string;
}): ShiftMutation {
  return {
    contract_version: 'shift_aggregate.v1',
    dataset_epoch: uuidSchema.parse(input.datasetEpoch),
    shift: {
      shift_id: uuidSchema.parse(input.shiftId),
      service_date: calendarDateSchema.parse(input.serviceDate ?? localCalendarDate()),
      actual_start: null,
      actual_end: null,
      home_base: null,
      shift_type: null,
      status: 'open',
      note: '',
      segment_order: [],
    },
    segments: [],
  };
}

export function createShiftEditorDraft(input: {
  readonly aggregate: ShiftAggregate;
  readonly uuid: AlphaUuidFactory;
  readonly targetTripSegmentId?: string;
}): ShiftEditorDraft {
  const aggregate = shiftAggregateSchema.parse(input.aggregate);
  assertOpenShift(aggregate);
  const target = input.targetTripSegmentId
    ? aggregate.segments.find(
        (segment) =>
          segment.trip_segment_id === uuidSchema.parse(input.targetTripSegmentId) &&
          segment.metadata.tombstone === null,
      )
    : undefined;
  if (input.targetTripSegmentId !== undefined && target === undefined) {
    throw new Error(`live trip segment ${input.targetTripSegmentId} is not part of the shift`);
  }
  if (target === undefined) return createAppendDraft(aggregate, input.uuid, null, null);

  const catalogVersion = target.data_versions.catalog;
  const routeSnapshot = target.route_snapshot;
  const routeDraftSnapshot = target.route_draft_snapshot ?? null;
  const origin =
    routeDraftSnapshot?.origin ??
    (target.origin
      ? endpointFromSnapshot(target.origin, catalogVersion)
      : emptyEndpoint(input.uuid, 'CZ'));
  const destination =
    routeDraftSnapshot?.destination ??
    (target.destination ? endpointFromSnapshot(target.destination, catalogVersion) : null);
  const routeSeed =
    routeSnapshot !== null
      ? normalizeRouteSeedForCurrentScoring(routeSeedFromSnapshot(routeSnapshot))
      : routeDraftSnapshot?.route_seed === null || routeDraftSnapshot?.route_seed === undefined
        ? null
        : normalizeRouteSeedForCurrentScoring(routeDraftSnapshot.route_seed);
  return shiftEditorDraftSchema.parse({
    contract_version: 'shift_editor_draft.v6',
    base_aggregate_revision: aggregate.aggregate_revision,
    shift_id: aggregate.shift.shift_id,
    trip_segment_id: target.trip_segment_id,
    route_variant_id:
      routeSnapshot?.route_variant_id ??
      routeDraftSnapshot?.route_variant_id ??
      nextUuid(input.uuid),
    service_date: aggregate.shift.service_date,
    actual_start: target.actual_start,
    actual_end: target.actual_end,
    origin,
    via:
      routeSnapshot?.via.map((snapshot) => endpointFromSnapshot(snapshot, catalogVersion)) ??
      routeDraftSnapshot?.via ??
      [],
    destination,
    planned_destination: target.planned_destination
      ? endpointFromSnapshot(target.planned_destination, catalogVersion)
      : null,
    early_termination_reason: target.early_termination_reason ?? '',
    route_selection:
      routeSnapshot === null
        ? (routeDraftSnapshot?.route_selection ?? null)
        : {
            kind: 'offer',
            offer_source: 'history',
            offer_id: routeOfferSelectionId('history', routeSnapshot.route_variant_id),
            selection_method: 'explicit',
          },
    route_seed: routeSeed,
    route_confirmation:
      routeSnapshot && destination
        ? {
            selection: {
              kind: 'offer',
              offer_source: 'history',
              offer_id: routeOfferSelectionId('history', routeSnapshot.route_variant_id),
              selection_method: 'explicit',
            },
            evidence: routeSnapshot.confirmation,
            origin: confirmedEndpoint(origin),
            via: routeSnapshot.via.map((snapshot) =>
              confirmedEndpoint(endpointFromSnapshot(snapshot, catalogVersion)),
            ),
            destination: confirmedEndpoint(destination),
            distance: {
              input_mode: 'inherited',
              value: routeSnapshot.distance,
            },
          }
        : null,
    route_copy_source:
      routeDraftSnapshot?.snapshot_version === 'route_draft_snapshot.v2'
        ? routeDraftSnapshot.route_copy_source
        : null,
    train_or_service_number: target.train_or_service_number ?? '',
    work_type: target.work_type,
    role: target.role,
    operator: target.operator ?? '',
    vehicles: target.vehicles.map((vehicle) => ({
      vehicle_id: vehicle.vehicle_id,
      series: vehicle.series,
      full_number: vehicle.full_number ?? '',
      nickname: vehicle.nickname ?? '',
      operator: vehicle.operator ?? '',
      vehicle_type: vehicle.vehicle_type,
    })),
    distance_input_mode:
      routeSnapshot === null ? (routeDraftSnapshot?.distance_input_mode ?? 'none') : 'inherited',
    distance_metres:
      routeSnapshot === null
        ? (routeDraftSnapshot?.distance_metres ?? null)
        : target.distance_metres,
    note: target.note,
    tags: target.tags,
    status: target.status,
  });
}

export function createNextShiftEditorDraft(input: {
  readonly aggregate: ShiftAggregate;
  readonly uuid: AlphaUuidFactory;
}): ShiftEditorDraft {
  const aggregate = shiftAggregateSchema.parse(input.aggregate);
  assertOpenShift(aggregate);
  const previous = liveSegments(aggregate)
    .toReversed()
    .find((segment) => segment.destination !== null);
  if (previous?.destination === null || previous?.destination === undefined) {
    throw new Error('save-and-next requires a previous actual destination snapshot');
  }
  return createAppendDraft(aggregate, input.uuid, previous.destination, previous);
}

export function createCopiedShiftEditorDraft(input: {
  readonly aggregate: ShiftAggregate;
  readonly source: TripSegment;
  readonly uuid: AlphaUuidFactory;
  readonly reverse?: boolean;
}): ShiftEditorDraft {
  const aggregate = shiftAggregateSchema.parse(input.aggregate);
  assertOpenShift(aggregate);
  const source = aggregate.segments.find(
    (segment) =>
      segment.trip_segment_id === input.source.trip_segment_id &&
      segment.metadata.tombstone === null,
  );
  if (source === undefined) {
    throw new Error('a copied segment must be live in the exact source aggregate');
  }
  const reverse = input.reverse === true;
  const sourceOrigin = reverse ? source.destination : source.origin;
  const sourceDestination = reverse ? source.origin : source.destination;
  const draft = createAppendDraft(aggregate, input.uuid, sourceOrigin, null);
  const routeSeed =
    reverse || source.route_snapshot === null
      ? null
      : normalizeRouteSeedForCurrentScoring(routeSeedFromSnapshot(source.route_snapshot));
  const via =
    source.route_snapshot === null
      ? []
      : (reverse ? source.route_snapshot.via.toReversed() : source.route_snapshot.via).map(
          (snapshot) =>
            endpointFromSnapshot(snapshot, source.route_snapshot?.catalog_version ?? null),
        );
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_variant_id:
      !reverse && source.route_snapshot !== null
        ? source.route_snapshot.route_variant_id
        : draft.route_variant_id,
    origin: sourceOrigin
      ? endpointFromSnapshot(sourceOrigin, source.data_versions.catalog)
      : draft.origin,
    destination: sourceDestination
      ? endpointFromSnapshot(sourceDestination, source.data_versions.catalog)
      : null,
    via,
    route_selection:
      reverse || source.route_snapshot === null
        ? { kind: 'manual' }
        : {
            kind: 'offer',
            offer_source: 'history',
            offer_id: routeOfferSelectionId('history', source.route_snapshot.route_variant_id),
            selection_method: 'explicit',
          },
    route_seed: routeSeed,
    route_confirmation: null,
    route_copy_source:
      reverse || source.route_snapshot === null
        ? null
        : {
            proof_contract: 'dmf.route-copy-source-proof.v1',
            source_shift_id: aggregate.shift.shift_id,
            source_trip_segment_id: source.trip_segment_id,
            source_trip_segment_revision: source.metadata.revision,
            source_route_variant_id: source.route_snapshot.route_variant_id,
            source_route_snapshot_sha256: routeCopySourceSnapshotSha256(source.route_snapshot),
          },
    train_or_service_number: source.train_or_service_number ?? '',
    work_type: source.work_type,
    role: source.role,
    operator: source.operator ?? '',
    vehicles: source.vehicles.map((vehicle) => ({
      vehicle_id: vehicle.vehicle_id,
      series: vehicle.series,
      full_number: vehicle.full_number ?? '',
      nickname: vehicle.nickname ?? '',
      operator: vehicle.operator ?? '',
      vehicle_type: vehicle.vehicle_type,
    })),
    distance_input_mode: routeSeed === null ? 'none' : 'inherited',
    distance_metres: routeSeed?.distance.metres ?? null,
    note: source.note,
    tags: source.tags,
    status: 'complete',
    planned_destination: null,
    early_termination_reason: '',
    actual_start: null,
    actual_end: null,
  });
}

export function createReversedShiftEditorDraft(input: {
  readonly aggregate: ShiftAggregate;
  readonly source: TripSegment;
  readonly uuid: AlphaUuidFactory;
}): ShiftEditorDraft {
  return createCopiedShiftEditorDraft({ ...input, reverse: true });
}

export function reverseDraftDirection(
  draftInput: ShiftEditorDraft,
  uuid: AlphaUuidFactory,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (draft.destination === null) {
    throw new Error('route reversal requires both actual endpoints');
  }
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_variant_id: nextUuid(uuid),
    origin: draft.destination,
    via: draft.via.toReversed(),
    destination: draft.origin,
    route_selection: { kind: 'manual' },
    route_seed: null,
    route_confirmation: null,
    route_copy_source: null,
    distance_input_mode: 'none',
    distance_metres: null,
  });
}

export function createRouteTemplateSourceSnapshot(
  source: TripSegment,
): RouteTemplateSourceSnapshot {
  const routeSnapshot = source.route_snapshot;
  if (
    source.metadata.tombstone !== null ||
    source.origin === null ||
    source.destination === null ||
    routeSnapshot === null
  ) {
    throw new Error('template source requires a live segment with a confirmed route snapshot');
  }
  return routeTemplateSourceSnapshotSchema.parse({
    snapshot_version: 'route_template_source_snapshot.v1',
    source_route_variant_id: routeSnapshot.route_variant_id,
    captured_at: routeSnapshot.captured_at,
    origin: source.origin,
    destination: source.destination,
    route_seed: routeSeedFromSnapshot(routeSnapshot),
    vehicles: source.vehicles,
  });
}

export function createTemplateShiftEditorDraft(input: {
  readonly aggregate: ShiftAggregate;
  readonly template: RouteTemplate;
  readonly uuid: AlphaUuidFactory;
}): ShiftEditorDraft {
  const aggregate = shiftAggregateSchema.parse(input.aggregate);
  assertOpenShift(aggregate);
  const template = routeTemplateSchema.parse(input.template);
  const snapshot = template.source_snapshot;
  if (snapshot === undefined) {
    throw new Error('legacy shallow template has no standalone source snapshot');
  }
  const draft = createAppendDraft(aggregate, input.uuid, snapshot.origin, null);
  const routeSeed = normalizeRouteSeedForCurrentScoring(snapshot.route_seed);
  const vehicleById = new Map(
    snapshot.vehicles.map((vehicle) => [vehicle.vehicle_id, vehicle] as const),
  );
  return shiftEditorDraftSchema.parse({
    ...draft,
    origin: endpointFromSnapshot(snapshot.origin, routeSeed.catalog_version),
    via: routeSeed.via.map((location) => endpointFromSnapshot(location, routeSeed.catalog_version)),
    destination: endpointFromSnapshot(snapshot.destination, routeSeed.catalog_version),
    route_selection: {
      kind: 'offer',
      offer_source: 'template',
      offer_id: routeOfferSelectionId('template', template.route_template_id),
      selection_method: 'explicit',
    },
    route_seed: routeSeed,
    route_confirmation: null,
    train_or_service_number: template.default_train_or_service_number ?? '',
    work_type: canonicalRouteTemplateWorkType(template.default_work_type),
    operator: template.default_operator ?? '',
    vehicles: template.default_vehicle_ids.map((vehicleId) => {
      const vehicle = vehicleById.get(vehicleId);
      if (vehicle === undefined) {
        throw new Error('template default vehicle is missing from its standalone snapshot');
      }
      return draftVehicleFromSnapshot(vehicle);
    }),
    distance_input_mode: 'inherited',
    distance_metres: routeSeed.distance.metres,
    actual_start: null,
    actual_end: null,
  });
}

export function updateDraftEndpoint(
  draftInput: ShiftEditorDraft,
  endpointName: DraftEndpointName,
  value: DraftEndpointUpdateValue,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const existing = draft[endpointName];
  const { endpoint, changed } = buildDraftEndpoint(existing, value, uuid);
  if (endpointName === 'planned_destination') {
    return shiftEditorDraftSchema.parse({ ...draft, planned_destination: endpoint });
  }
  if (!changed) return draftInput;
  return invalidateDraftRouteShape(
    { ...draft, [endpointName]: endpoint },
    uuid,
    options,
    draft.route_seed !== null || draft.route_selection?.kind === 'offer',
  );
}

export function effectiveDraftVia(
  draftInput: ShiftEditorDraft,
): readonly ShiftEditorDraftEndpoint[] {
  return shiftEditorDraftSchema.parse(draftInput).via;
}

export function addDraftVia(
  draftInput: ShiftEditorDraft,
  value: DraftEndpointUpdateValue,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const { endpoint } = buildDraftEndpoint(null, value, uuid);
  return invalidateDraftRouteShape(
    { ...draft, via: [...draft.via, endpoint] },
    uuid,
    options,
    true,
  );
}

export function duplicateDraftVia(
  draftInput: ShiftEditorDraft,
  index: number,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const endpoint = draft.via[index];
  if (endpoint === undefined) throw new RangeError('via index is out of range');
  return invalidateDraftRouteShape(
    { ...draft, via: [...draft.via, endpoint] },
    uuid,
    options,
    true,
  );
}

export function updateDraftVia(
  draftInput: ShiftEditorDraft,
  index: number,
  value: DraftEndpointUpdateValue,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const existing = draft.via[index];
  if (existing === undefined) throw new RangeError('via index is out of range');
  const { endpoint, changed } = buildDraftEndpoint(existing, value, uuid);
  if (!changed) return draftInput;
  return invalidateDraftRouteShape(
    { ...draft, via: draft.via.map((item, itemIndex) => (itemIndex === index ? endpoint : item)) },
    uuid,
    options,
    true,
  );
}

export function removeDraftVia(
  draftInput: ShiftEditorDraft,
  index: number,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (draft.via[index] === undefined) throw new RangeError('via index is out of range');
  return invalidateDraftRouteShape(
    { ...draft, via: draft.via.filter((_, itemIndex) => itemIndex !== index) },
    uuid,
    options,
    true,
  );
}

export function moveDraftVia(
  draftInput: ShiftEditorDraft,
  fromIndex: number,
  toIndex: number,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (draft.via[fromIndex] === undefined || draft.via[toIndex] === undefined) {
    throw new RangeError('via index is out of range');
  }
  if (fromIndex === toIndex) return draftInput;
  const via = [...draft.via];
  const [moved] = via.splice(fromIndex, 1);
  if (moved === undefined) throw new RangeError('via index is out of range');
  via.splice(toIndex, 0, moved);
  return invalidateDraftRouteShape({ ...draft, via }, uuid, options, true);
}

export function selectManualDraftRoute(
  draftInput: ShiftEditorDraft,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (
    draft.route_selection?.kind === 'manual' &&
    draft.route_seed === null &&
    draft.route_confirmation === null
  ) {
    return draftInput;
  }
  const selected = invalidateDraftRouteShape(draft, uuid, options, true);
  return draft.distance_input_mode === 'manual_override'
    ? shiftEditorDraftSchema.parse({
        ...selected,
        distance_input_mode: 'manual_override',
        distance_metres: draft.distance_metres,
      })
    : selected;
}

export function applyDraftRouteSeed(
  draftInput: ShiftEditorDraft,
  input: ApplyDraftRouteSeedInput,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const routeSeed = normalizeRouteSeedForCurrentScoring(input.route_seed);
  if (
    input.via.length !== routeSeed.via.length ||
    input.via.some((location, index) => location.location_id !== routeSeed.via[index]?.location_id)
  ) {
    throw new Error('route offer via snapshots must exactly match its route seed');
  }
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_variant_id: uuidSchema.parse(input.route_variant_id),
    origin: endpointFromSnapshot(input.origin, routeSeed.catalog_version),
    via: input.via.map((location) => endpointFromSnapshot(location, routeSeed.catalog_version)),
    destination: endpointFromSnapshot(input.destination, routeSeed.catalog_version),
    route_selection: input.route_selection,
    route_seed: routeSeed,
    route_confirmation: null,
    route_copy_source: input.route_copy_source,
    distance_input_mode: 'inherited',
    distance_metres: routeSeed.distance.metres,
  });
}

export function updateDraftDistanceMetres(
  draftInput: ShiftEditorDraft,
  distanceMetres: ShiftEditorDraft['distance_metres'],
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const distanceInputMode = distanceMetres === null ? 'none' : 'manual_override';
  if (draft.distance_input_mode === distanceInputMode && draft.distance_metres === distanceMetres) {
    return draftInput;
  }
  return shiftEditorDraftSchema.parse({
    ...draft,
    distance_input_mode: distanceInputMode,
    distance_metres: distanceMetres,
    route_confirmation: null,
  });
}

export function confirmManualRoute(
  draftInput: ShiftEditorDraft,
  confirmedAtInput: string,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (draft.route_selection === null) {
    throw new AlphaShiftDraftValidationError([
      {
        field: 'route_confirmation',
        message: 'Před potvrzením výslovně vyber nabízenou nebo jinou trasu.',
      },
    ]);
  }
  const origin = confirmedEndpoint(draft.origin);
  if (draft.destination === null) {
    throw new AlphaShiftDraftValidationError([
      { field: 'destination', message: 'Zadej platné provozní místo Kam.' },
    ]);
  }
  const destination = confirmedEndpoint(draft.destination);
  const confirmedAt = instantSchema.parse(confirmedAtInput);
  const routeSeed = draft.route_seed ?? null;
  const distance = shiftEditorDraftDistanceForConfirmation(draft, confirmedAt);
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_confirmation: {
      selection: draft.route_selection,
      evidence: {
        kind: 'explicit_user_confirmation',
        confirmed_at: confirmedAt,
        fingerprint_contract: 'dmf.route-variant-identity.sha256.v1',
        route_fingerprint_sha256: routeFingerprintSha256({
          candidate_source: routeSeed?.candidate_source ?? 'manual',
          destination_location_id: destination.location_id,
          graph_sha256: routeSeed?.graph_version?.sha256 ?? null,
          network_edge_ids:
            routeSeed?.network_segments.map((segment) => segment.graph_edge_id) ?? [],
          origin_location_id: origin.location_id,
          via_location_ids: draft.via.map((endpoint) => endpoint.location_id),
        }),
      },
      origin,
      via: draft.via.map(confirmedEndpoint),
      destination,
      distance: {
        input_mode: draft.distance_input_mode,
        value: distance,
      },
    },
  });
}

export function matchesPersistedHistoricalRouteSelection(
  draftInput: ShiftEditorDraft,
  aggregateInput: ShiftAggregate,
): boolean {
  const draftResult = shiftEditorDraftSchema.safeParse(draftInput);
  const aggregateResult = shiftAggregateSchema.safeParse(aggregateInput);
  if (!draftResult.success || !aggregateResult.success) return false;
  const draft = draftResult.data;
  const aggregate = aggregateResult.data;
  const selection = draft.route_selection;
  const confirmation = draft.route_confirmation;
  if (
    draft.shift_id !== aggregate.shift.shift_id ||
    draft.base_aggregate_revision !== aggregate.aggregate_revision ||
    selection?.kind !== 'offer' ||
    selection.offer_source !== 'history' ||
    selection.selection_method !== 'explicit' ||
    confirmation === null ||
    confirmation.selection.kind !== 'offer' ||
    confirmation.selection.offer_source !== 'history' ||
    confirmation.selection.offer_id !== selection.offer_id ||
    confirmation.selection.selection_method !== selection.selection_method
  ) {
    return false;
  }
  const target = aggregate.segments.find(
    (segment) =>
      segment.trip_segment_id === draft.trip_segment_id && segment.metadata.tombstone === null,
  );
  const snapshot = target?.route_snapshot ?? null;
  return (
    snapshot !== null &&
    snapshot.route_variant_id === draft.route_variant_id &&
    selection.offer_id === routeOfferSelectionId('history', snapshot.route_variant_id) &&
    confirmation.evidence.route_fingerprint_sha256 ===
      snapshot.confirmation.route_fingerprint_sha256
  );
}

export function bindDraftToRouteVariant(
  draftInput: ShiftEditorDraft,
  routeInput: RouteVariant,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const route = routeVariantSchema.parse(routeInput);
  if (!routeVariantMatchesConfirmedDraft(draft, route)) {
    throw new Error('known route variant does not match the explicitly confirmed route candidate');
  }
  return shiftEditorDraftSchema.parse({ ...draft, route_variant_id: route.route_variant_id });
}

export function routeVariantMatchesConfirmedDraft(
  draftInput: ShiftEditorDraft,
  routeInput: RouteVariant,
): boolean {
  const draftResult = shiftEditorDraftSchema.safeParse(draftInput);
  const routeResult = routeVariantSchema.safeParse(routeInput);
  if (!draftResult.success || !routeResult.success) return false;
  const draft = draftResult.data;
  const route = routeResult.data;
  const confirmation = draft.route_confirmation;
  const routeSeed = draft.route_seed ?? null;
  if (route.metadata.tombstone !== null || confirmation === null) return false;
  const fingerprint = routeVariantSemanticFingerprint(route);
  return (
    fingerprint === confirmation.evidence.route_fingerprint_sha256 &&
    (route.confirmation === null
      ? route.use_count === 0 && route.explicit_confirmation_count === 0
      : route.confirmation.route_fingerprint_sha256 === fingerprint) &&
    route.origin_location_id === confirmation.origin.location_id &&
    route.destination_location_id === confirmation.destination.location_id &&
    route.candidate_source === (routeSeed?.candidate_source ?? 'manual') &&
    JSON.stringify(route.via_location_ids) ===
      JSON.stringify(draft.via.map((endpoint) => endpoint.location_id)) &&
    JSON.stringify(route.network_segments) === JSON.stringify(routeSeed?.network_segments ?? []) &&
    route.graph_version?.sha256 === routeSeed?.graph_version?.sha256
  );
}

function routeVariantSemanticFingerprint(route: RouteVariant): string {
  return routeFingerprintSha256({
    candidate_source: route.candidate_source,
    destination_location_id: route.destination_location_id,
    graph_sha256: route.graph_version?.sha256 ?? null,
    network_edge_ids: route.network_segments.map((segment) => segment.graph_edge_id),
    origin_location_id: route.origin_location_id,
    via_location_ids: route.via_location_ids,
  });
}

export function endpointToLocationSnapshot(
  endpointInput: ShiftEditorDraftEndpoint,
): LocationSnapshot {
  const endpoint = shiftEditorDraftEndpointSchema.parse(endpointInput);
  if (endpoint.carried_location_snapshot !== null) return endpoint.carried_location_snapshot;
  const finalEndpoint = confirmedEndpoint(endpoint);
  return {
    location_id: finalEndpoint.location_id,
    display_name: finalEndpoint.name,
    location_type: 'operational_point',
    country_code: finalEndpoint.country_code,
    key_identifiers: finalEndpoint.key_identifier === null ? [] : [finalEndpoint.key_identifier],
  };
}

export function validateShiftEditorDraftForCommit(
  draftInput: ShiftEditorDraft,
): ShiftDraftValidationResult {
  const parsed = shiftEditorDraftSchema.safeParse(draftInput);
  if (!parsed.success) {
    return {
      valid: false,
      issues: parsed.error.issues.map((issue) => ({
        field: issueField(issue.path[0]),
        message: issue.message,
      })),
    };
  }
  const draft = parsed.data;
  const issues: ShiftDraftValidationIssue[] = [];
  const performanceRecord = draft.status === 'complete' || draft.status === 'partial';
  const originValid = confirmedEndpointResult(draft.origin).success;
  const destinationValid =
    draft.destination !== null && confirmedEndpointResult(draft.destination).success;
  if (performanceRecord && !originValid) {
    issues.push({ field: 'origin', message: 'Zadej platný název a zemi provozního místa Odkud.' });
  }
  if (performanceRecord && !destinationValid) {
    issues.push({
      field: 'destination',
      message: 'Zadej platný název a zemi provozního místa Kam.',
    });
  }
  if (performanceRecord && draft.route_confirmation === null) {
    issues.push({
      field: 'route_confirmation',
      message: 'Před uložením výslovně potvrď skutečnou trasu.',
    });
  }
  if (performanceRecord && draft.route_selection === null) {
    issues.push({
      field: 'route_confirmation',
      message: 'Před uložením výslovně vyber nabízenou nebo jinou trasu.',
    });
  }
  if (draft.status === 'partial') {
    if (
      draft.planned_destination === null ||
      !confirmedEndpointResult(draft.planned_destination).success
    ) {
      issues.push({
        field: 'planned_destination',
        message: 'U předčasně ukončeného úseku zadej původně plánovaný cíl.',
      });
    }
    if (draft.early_termination_reason.trim() === '') {
      issues.push({ field: 'partial', message: 'Zadej důvod předčasného ukončení.' });
    }
    if (
      draft.planned_destination !== null &&
      draft.planned_destination.location_id === draft.destination?.location_id
    ) {
      issues.push({
        field: 'planned_destination',
        message: 'Plánovaný cíl musí být jiný než skutečně dosažený cíl.',
      });
    }
  } else if (draft.planned_destination !== null || draft.early_termination_reason !== '') {
    issues.push({
      field: 'partial',
      message: 'Plánovaný cíl a důvod jsou vyhrazené jen předčasně ukončenému úseku.',
    });
  }
  draft.vehicles.forEach((vehicle, index) => {
    if (vehicle.series.trim() === '' || vehicle.vehicle_type === null) {
      issues.push({
        field: 'vehicles',
        message: `U vozidla ${String(index + 1)} zadej řadu a typ, nebo řádek odeber.`,
      });
    }
  });
  if (
    (draft.status === 'complete' || draft.status === 'partial') &&
    draft.distance_metres !== null &&
    draft.route_confirmation === null
  ) {
    issues.push({
      field: 'distance',
      message: 'Kilometráž vyžaduje výslovně potvrzenou skutečnou trasu.',
    });
  }
  return issues.length === 0 ? { valid: true, issues: [] } : { valid: false, issues };
}

export function withoutRevisionMetadata<T extends { readonly metadata: unknown }>(
  value: T,
): Omit<T, 'metadata'> {
  const { metadata, ...withoutMetadata } = value;
  void metadata;
  return withoutMetadata;
}

export function shiftAggregateToMutation(aggregateInput: ShiftAggregate): ShiftMutation {
  const aggregate = shiftAggregateSchema.parse(aggregateInput);
  return {
    contract_version: aggregate.contract_version,
    dataset_epoch: aggregate.dataset_epoch,
    shift: withoutRevisionMetadata(aggregate.shift),
    segments: aggregate.segments.map(withoutRevisionMetadata),
  };
}

export function buildShiftDraftBundleMutation(input: {
  readonly aggregate: ShiftAggregate;
  readonly draft: ShiftEditorDraft;
  readonly existingRouteVariant?: RouteVariant | null;
  readonly existingVehicles?: readonly Vehicle[];
}): ShiftDraftBundleMutation {
  const aggregate = shiftAggregateSchema.parse(input.aggregate);
  assertOpenShift(aggregate);
  const draft = shiftEditorDraftSchema.parse(input.draft);
  const validation = validateShiftEditorDraftForCommit(draft);
  if (!validation.valid) throw new AlphaShiftDraftValidationError(validation.issues);
  if (draft.shift_id !== aggregate.shift.shift_id)
    throw new Error('draft belongs to a different shift');
  if (draft.base_aggregate_revision !== aggregate.aggregate_revision) {
    throw new Error('draft base revision does not match the persisted shift');
  }
  if (draft.service_date !== aggregate.shift.service_date) {
    throw new Error('segment commit cannot silently change the shift service date');
  }
  assertDurableRouteCopySource(draft, aggregate);
  const performanceRecord = draft.status === 'complete' || draft.status === 'partial';

  const previousTarget = aggregate.segments.find(
    (segment) =>
      segment.trip_segment_id === draft.trip_segment_id && segment.metadata.tombstone === null,
  );
  const expandedRouteDraftWireAvailable =
    ROUTE_DRAFT_WIRE_WRITE_MODE === 'expanded_writer' ||
    (previousTarget !== undefined && Object.hasOwn(previousTarget, 'route_draft_snapshot'));
  if (!performanceRecord && !expandedRouteDraftWireAvailable && hasExpandedRouteDraftState(draft)) {
    throw new AlphaShiftDraftValidationError([
      {
        field: 'route_confirmation',
        message:
          'Kompatibilní mezirelease zatím neuloží rozpracovanou nebo zrušenou jízdu s rozepsaným místem, vybranou trasou, body přes ani kilometráží. Ulož ji jako dokončenou, nebo nejdřív zruš rozšířenou volbu trasy.',
      },
    ]);
  }
  const existingRoute = input.existingRouteVariant
    ? routeVariantSchema.parse(input.existingRouteVariant)
    : null;
  if (existingRoute !== null && existingRoute.metadata.tombstone !== null) {
    throw new Error('a deleted route variant cannot be reused');
  }
  const existingVehicleById = new Map(
    (input.existingVehicles ?? []).map((vehicle) => {
      const parsed = vehicleSchema.parse(vehicle);
      if (parsed.metadata.tombstone !== null) {
        throw new Error('a deleted vehicle cannot be reused');
      }
      return [parsed.vehicle_id, parsed] as const;
    }),
  );
  const routeConfirmation = draft.route_confirmation;
  const origin = routeDraftEndpointLocationSnapshot(draft.origin);
  const destination =
    draft.destination === null ? null : routeDraftEndpointLocationSnapshot(draft.destination);
  const plannedDestination =
    draft.planned_destination === null
      ? null
      : routeDraftEndpointLocationSnapshot(draft.planned_destination);
  const via = draft.via.flatMap((endpoint) => {
    const snapshot = routeDraftEndpointLocationSnapshot(endpoint);
    return snapshot === null ? [] : [snapshot];
  });
  const routeSeed =
    draft.route_seed === null ? null : normalizeRouteSeedForCurrentScoring(draft.route_seed);
  const endpointCatalogVersion = catalogVersionForEndpoints(
    draft.origin,
    ...draft.via,
    draft.destination,
    draft.planned_destination,
  );
  const catalogVersion = mergeRouteCatalogVersion(
    routeSeed?.catalog_version ?? null,
    endpointCatalogVersion,
  );
  const capturedAt = routeConfirmation?.evidence.confirmed_at ?? null;
  const distance = routeConfirmation?.distance.value ?? null;

  const routeSnapshot =
    !performanceRecord ||
    routeConfirmation === null ||
    origin === null ||
    destination === null ||
    capturedAt === null ||
    distance === null
      ? null
      : {
          snapshot_version: 'route_snapshot.v1' as const,
          route_variant_id: draft.route_variant_id,
          captured_at: capturedAt,
          origin,
          via,
          destination,
          network_segments: routeSeed?.network_segments ?? [],
          distance,
          candidate_source: routeSeed?.candidate_source ?? ('manual' as const),
          confirmation: routeConfirmation.evidence,
          catalog_version: catalogVersion,
          graph_version: routeSeed?.graph_version ?? null,
          route_scoring_version:
            routeSeed?.route_scoring_version ?? ALPHA_MANUAL_ROUTE_SCORING_VERSION,
        };
  const routeDraftSnapshot = performanceRecord
    ? null
    : routeDraftSnapshotSchema.parse({
        snapshot_version: 'route_draft_snapshot.v2',
        route_variant_id: draft.route_variant_id,
        origin: draft.origin,
        via: draft.via,
        destination: draft.destination,
        route_selection: draft.route_selection,
        route_seed: routeSeed,
        route_copy_source: draft.route_copy_source,
        distance_input_mode: draft.distance_input_mode,
        distance_metres: draft.distance_metres,
      });
  const vehicleSnapshots = draft.vehicles.map((vehicle) => ({
    vehicle_id: vehicle.vehicle_id,
    series: requiredTrimmed(vehicle.series, 'vehicle series'),
    full_number: nullableTrimmed(vehicle.full_number),
    nickname: nullableTrimmed(vehicle.nickname),
    operator: nullableTrimmed(vehicle.operator),
    vehicle_type: requiredVehicleType(vehicle.vehicle_type),
  }));
  const previousWithoutMetadata = previousTarget ? withoutRevisionMetadata(previousTarget) : null;
  const targetSegment: ShiftMutation['segments'][number] = {
    ...(previousWithoutMetadata ?? {
      trip_segment_id: draft.trip_segment_id,
      shift_id: aggregate.shift.shift_id,
      order: aggregate.segments.length,
    }),
    actual_start: draft.actual_start,
    actual_end: draft.actual_end,
    origin,
    destination,
    planned_destination: draft.status === 'partial' ? plannedDestination : null,
    early_termination_reason:
      draft.status === 'partial' ? requiredTrimmed(draft.early_termination_reason, 'reason') : null,
    route_snapshot: routeSnapshot,
    ...(expandedRouteDraftWireAvailable ? { route_draft_snapshot: routeDraftSnapshot } : {}),
    train_or_service_number: nullableTrimmed(draft.train_or_service_number),
    work_type: draft.work_type,
    role: draft.role,
    operator: nullableTrimmed(draft.operator),
    vehicles: vehicleSnapshots,
    distance_metres: routeSnapshot?.distance.metres ?? null,
    note: draft.note,
    tags: draft.tags,
    status: draft.status,
    data_versions: {
      catalog: catalogVersion,
      graph: routeSeed?.graph_version ?? null,
      route_scoring_version: routeSeed?.route_scoring_version ?? ALPHA_MANUAL_ROUTE_SCORING_VERSION,
    },
  };
  const previousSegments = aggregate.segments.map(withoutRevisionMetadata);
  const targetIndex = previousSegments.findIndex(
    (segment) => segment.trip_segment_id === draft.trip_segment_id,
  );
  const segments =
    targetIndex === -1
      ? [...previousSegments, targetSegment]
      : previousSegments.map((segment, index) => (index === targetIndex ? targetSegment : segment));
  const orderedSegments = segments.map((segment, order) => ({ ...segment, order }));
  const shiftMutation: ShiftMutation = {
    contract_version: aggregate.contract_version,
    dataset_epoch: aggregate.dataset_epoch,
    shift: {
      ...withoutRevisionMetadata(aggregate.shift),
      segment_order: orderedSegments.map((segment) => segment.trip_segment_id),
    },
    segments: orderedSegments,
  };

  let routeVariant: ShiftDraftBundleMutation['route_variant'] = null;
  if (
    routeSnapshot !== null &&
    capturedAt !== null &&
    origin !== null &&
    destination !== null &&
    distance !== null
  ) {
    if (existingRoute !== null && existingRoute.route_variant_id !== draft.route_variant_id) {
      throw new Error('existing route variant does not match the draft route identity');
    }
    const preservesExistingUse =
      previousTarget?.route_snapshot?.route_variant_id === draft.route_variant_id;
    const materializesLegacyExistingUse = preservesExistingUse && existingRoute === null;
    const usageContextBase = {
      direction_key: `${origin.location_id}>${destination.location_id}`,
      train_or_service_number: nullableTrimmed(draft.train_or_service_number),
      work_type: draft.work_type,
      operator: nullableTrimmed(draft.operator),
    };
    const usageContexts =
      vehicleSnapshots.length === 0
        ? [{ ...usageContextBase, vehicle_id: null }]
        : vehicleSnapshots.map((vehicle) => ({
            ...usageContextBase,
            vehicle_id: vehicle.vehicle_id,
          }));
    const routeValue: Omit<RouteVariant, 'metadata'> = {
      route_variant_id: draft.route_variant_id,
      origin_location_id: origin.location_id,
      destination_location_id: destination.location_id,
      via_location_ids: via.map((location) => location.location_id),
      network_segments: routeSeed?.network_segments ?? [],
      distance,
      candidate_source: routeSeed?.candidate_source ?? 'manual',
      confirmation: routeConfirmation?.evidence ?? null,
      explicit_confirmation_count: preservesExistingUse
        ? (existingRoute?.explicit_confirmation_count ?? 1)
        : (existingRoute?.explicit_confirmation_count ?? 0) + 1,
      use_count: preservesExistingUse
        ? (existingRoute?.use_count ?? 1)
        : (existingRoute?.use_count ?? 0) + 1,
      last_used_at: preservesExistingUse ? (existingRoute?.last_used_at ?? capturedAt) : capturedAt,
      favorite: existingRoute?.favorite ?? false,
      default_for_context: existingRoute?.default_for_context ?? false,
      usage_contexts: usageContexts.reduce(
        (contexts, usageContext) => mergeUsageContexts(contexts, usageContext),
        existingRoute?.usage_contexts ?? [],
      ),
      catalog_version: catalogVersion,
      graph_version: routeSeed?.graph_version ?? null,
      route_scoring_version: routeSeed?.route_scoring_version ?? ALPHA_MANUAL_ROUTE_SCORING_VERSION,
    };
    routeVariantSchema.parse({
      ...routeValue,
      metadata: {
        created_at: capturedAt,
        updated_at: capturedAt,
        revision: 1,
        tombstone: null,
      },
    });
    routeVariant = {
      value: routeValue,
      expected_revision: existingRoute?.metadata.revision ?? null,
      route_usage_change: materializesLegacyExistingUse
        ? 'materialize_legacy_existing_use'
        : preservesExistingUse
          ? 'preserve_existing_use'
          : 'record_new_use',
    };
  }

  const metadataForValidation = {
    created_at: capturedAt ?? aggregate.shift.metadata.updated_at,
    updated_at: capturedAt ?? aggregate.shift.metadata.updated_at,
    revision: 1,
    tombstone: null,
  } as const;
  const locationMutations = collectLocationMutations(
    [draft.origin, ...draft.via, draft.destination, draft.planned_destination],
    metadataForValidation,
  );
  const vehicleMutations: ShiftDraftBundleMutation['vehicles'] = draft.vehicles.map(
    (vehicle, index) => {
      const snapshot = vehicleSnapshots[index];
      if (snapshot === undefined) throw new Error('vehicle snapshot projection is incomplete');
      const existing = existingVehicleById.get(vehicle.vehicle_id) ?? null;
      const preservesExistingUse =
        previousTarget?.vehicles.some(
          (previousVehicle) => previousVehicle.vehicle_id === vehicle.vehicle_id,
        ) === true && existing !== null;
      const value = vehicleSchema.parse({
        ...snapshot,
        favorite: existing?.favorite ?? false,
        last_used_at: preservesExistingUse
          ? existing.last_used_at
          : (capturedAt ?? existing?.last_used_at ?? null),
        lifecycle: existing?.lifecycle ?? 'active',
        metadata: metadataForValidation,
      });
      return {
        value: withoutRevisionMetadata(value),
        expected_revision: existing?.metadata.revision ?? null,
      };
    },
  );

  shiftAggregateSchema.parse({
    ...shiftMutation,
    aggregate_revision: aggregate.aggregate_revision + 1,
    shift: { ...shiftMutation.shift, metadata: metadataForValidation },
    segments: shiftMutation.segments.map((segment) => ({
      ...segment,
      metadata: metadataForValidation,
    })),
  });
  return {
    dataset_epoch: aggregate.dataset_epoch,
    target_trip_segment_id: draft.trip_segment_id,
    user_defined_locations: locationMutations,
    vehicles: vehicleMutations,
    route_variant: routeVariant,
    shift_aggregate: {
      value: shiftMutation,
      expected_revision: aggregate.aggregate_revision,
    },
  };
}

function createAppendDraft(
  aggregate: ShiftAggregate,
  uuid: AlphaUuidFactory,
  carriedOrigin: LocationSnapshot | null,
  context: TripSegment | null,
): ShiftEditorDraft {
  return shiftEditorDraftSchema.parse({
    contract_version: 'shift_editor_draft.v6',
    base_aggregate_revision: aggregate.aggregate_revision,
    shift_id: aggregate.shift.shift_id,
    trip_segment_id: nextUuid(uuid),
    route_variant_id: nextUuid(uuid),
    service_date: aggregate.shift.service_date,
    actual_start: null,
    actual_end: null,
    origin: carriedOrigin
      ? endpointFromSnapshot(carriedOrigin, context?.data_versions.catalog ?? null)
      : emptyEndpoint(uuid, 'CZ'),
    via: [],
    destination: null,
    planned_destination: null,
    early_termination_reason: '',
    route_selection: null,
    route_seed: null,
    route_confirmation: null,
    route_copy_source: null,
    train_or_service_number: context?.train_or_service_number ?? '',
    work_type: null,
    role: context?.role ?? null,
    operator: context?.operator ?? '',
    vehicles:
      context?.vehicles.map((vehicle) => ({
        vehicle_id: vehicle.vehicle_id,
        series: vehicle.series,
        full_number: vehicle.full_number ?? '',
        nickname: vehicle.nickname ?? '',
        operator: vehicle.operator ?? '',
        vehicle_type: vehicle.vehicle_type,
      })) ?? [],
    distance_input_mode: 'none',
    distance_metres: null,
    note: '',
    tags: [],
    status: 'complete',
  });
}

function buildDraftEndpoint(
  existing: ShiftEditorDraftEndpoint | null,
  value: DraftEndpointUpdateValue,
  uuid: AlphaUuidFactory,
): { readonly endpoint: ShiftEditorDraftEndpoint; readonly changed: boolean } {
  const selectedSnapshot = value.carried_location_snapshot;
  if ((value.catalog_document_id === undefined) !== (value.catalog_version === undefined)) {
    throw new Error('catalog selection requires both document ID and artifact version');
  }
  const changed =
    existing === null ||
    existing.name !== value.name ||
    existing.country_code !== value.country_code ||
    (selectedSnapshot !== undefined && existing.location_id !== selectedSnapshot.location_id) ||
    (Object.hasOwn(value, 'key_identifier') &&
      !sameDraftIdentifier(existing.key_identifier, value.key_identifier ?? null));
  const manualIdentityReplaced = selectedSnapshot === undefined && changed;
  const endpoint = shiftEditorDraftEndpointSchema.parse({
    location_id:
      selectedSnapshot?.location_id ??
      (existing === null || (existing.carried_location_snapshot !== null && manualIdentityReplaced)
        ? nextUuid(uuid)
        : existing.location_id),
    name: value.name,
    country_code: value.country_code,
    key_identifier:
      selectedSnapshot?.key_identifiers[0] ??
      (Object.hasOwn(value, 'key_identifier')
        ? (value.key_identifier ?? null)
        : changed
          ? null
          : (existing?.key_identifier ?? null)),
    carried_location_snapshot:
      selectedSnapshot ?? (changed ? null : (existing?.carried_location_snapshot ?? null)),
    catalog_document_id:
      selectedSnapshot === undefined
        ? changed
          ? null
          : (existing?.catalog_document_id ?? null)
        : (value.catalog_document_id ?? null),
    catalog_version:
      selectedSnapshot === undefined
        ? changed
          ? null
          : (existing?.catalog_version ?? null)
        : (value.catalog_version ?? null),
  });
  return { endpoint, changed };
}

function invalidateDraftRouteShape(
  draftInput: ShiftEditorDraft,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
  selectManual: boolean,
): ShiftEditorDraft {
  // Callers may already have replaced one endpoint or ordered via entry, so the
  // intermediate object is intentionally not reparsed until stale route claims
  // and confirmation have been cleared below.
  const draft = draftInput;
  const persistedRouteVariantId =
    options.persistedRouteVariantId === null
      ? null
      : uuidSchema.parse(options.persistedRouteVariantId);
  const requiresFork =
    draft.route_variant_id === persistedRouteVariantId ||
    draft.route_seed !== null ||
    draft.route_selection?.kind === 'offer';
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_variant_id: requiresFork ? nextUuid(uuid) : draft.route_variant_id,
    route_selection: selectManual ? { kind: 'manual' } : draft.route_selection,
    route_seed: null,
    route_confirmation: null,
    route_copy_source: null,
    distance_input_mode: 'none',
    distance_metres: null,
  });
}

function emptyEndpoint(
  uuid: AlphaUuidFactory,
  countryCode: ShiftEditorDraftEndpoint['country_code'],
): ShiftEditorDraftEndpoint {
  return shiftEditorDraftEndpointSchema.parse({
    location_id: nextUuid(uuid),
    name: '',
    country_code: countryCode,
    key_identifier: null,
    carried_location_snapshot: null,
    catalog_document_id: null,
    catalog_version: null,
  });
}

function endpointFromSnapshot(
  snapshot: LocationSnapshot,
  catalogVersion: ArtifactVersion | null,
): ShiftEditorDraftEndpoint {
  const documentId = catalogVersion === null ? null : catalogDocumentId(snapshot);
  return shiftEditorDraftEndpointSchema.parse({
    location_id: snapshot.location_id,
    name: snapshot.display_name,
    country_code: snapshot.country_code,
    key_identifier: snapshot.key_identifiers[0] ?? null,
    carried_location_snapshot: snapshot,
    catalog_document_id: documentId,
    catalog_version: documentId === null ? null : catalogVersion,
  });
}

function routeSeedFromSnapshot(
  snapshot: RouteSnapshot,
  candidateSource: RouteSeed['candidate_source'] = snapshot.candidate_source,
): RouteSeed {
  return {
    candidate_source: candidateSource,
    via: snapshot.via,
    network_segments: snapshot.network_segments,
    distance: snapshot.distance,
    catalog_version: snapshot.catalog_version,
    graph_version: snapshot.graph_version,
    route_scoring_version: snapshot.route_scoring_version,
  };
}

function assertDurableRouteCopySource(draft: ShiftEditorDraft, aggregate: ShiftAggregate): void {
  const proof = draft.route_copy_source;
  if (proof === null) return;
  const source = aggregate.segments.find(
    (segment) =>
      segment.trip_segment_id === proof.source_trip_segment_id &&
      segment.metadata.tombstone === null,
  );
  const snapshot = source?.route_snapshot ?? null;
  if (
    proof.source_shift_id !== aggregate.shift.shift_id ||
    source === undefined ||
    source.metadata.revision !== proof.source_trip_segment_revision ||
    snapshot === null ||
    snapshot.route_variant_id !== proof.source_route_variant_id ||
    routeCopySourceSnapshotSha256(snapshot) !== proof.source_route_snapshot_sha256
  ) {
    throw new Error('copied route source is stale, deleted, foreign, or forged');
  }

  const origin = routeDraftEndpointLocationSnapshot(draft.origin);
  const destination =
    draft.destination === null ? null : routeDraftEndpointLocationSnapshot(draft.destination);
  const via = draft.via.map(routeDraftEndpointLocationSnapshot);
  const expectedSeed = normalizeRouteSeedForCurrentScoring(routeSeedFromSnapshot(snapshot));
  if (
    origin === null ||
    destination === null ||
    via.some((value) => value === null) ||
    canonicalJsonStringify(origin satisfies JsonValue) !==
      canonicalJsonStringify(snapshot.origin satisfies JsonValue) ||
    canonicalJsonStringify(destination satisfies JsonValue) !==
      canonicalJsonStringify(snapshot.destination satisfies JsonValue) ||
    canonicalJsonStringify(via satisfies JsonValue) !==
      canonicalJsonStringify(snapshot.via satisfies JsonValue) ||
    canonicalJsonStringify(draft.route_seed satisfies JsonValue) !==
      canonicalJsonStringify(expectedSeed satisfies JsonValue) ||
    draft.route_selection?.kind !== 'offer' ||
    draft.route_selection.offer_source !== 'history' ||
    draft.route_selection.selection_method !== 'explicit' ||
    draft.route_selection.offer_id !== routeOfferSelectionId('history', draft.route_variant_id)
  ) {
    throw new Error('copied route draft no longer matches its exact source snapshot');
  }
}

export function hasExpandedRouteDraftState(draftInput: ShiftEditorDraft): boolean {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  return (
    hasUnprojectableLegacyEndpointState(draft.origin) ||
    (draft.destination !== null && hasUnprojectableLegacyEndpointState(draft.destination)) ||
    draft.planned_destination !== null ||
    draft.route_selection !== null ||
    draft.route_seed !== null ||
    draft.route_copy_source !== null ||
    draft.via.length > 0 ||
    draft.distance_input_mode !== 'none' ||
    draft.distance_metres !== null
  );
}

function hasUnprojectableLegacyEndpointState(endpoint: ShiftEditorDraftEndpoint): boolean {
  const snapshot = routeDraftEndpointLocationSnapshot(endpoint);
  if (snapshot === null) {
    return (
      endpoint.name !== '' ||
      endpoint.country_code !== 'CZ' ||
      endpoint.key_identifier !== null ||
      endpoint.carried_location_snapshot !== null ||
      endpoint.catalog_document_id !== null ||
      endpoint.catalog_version !== null
    );
  }
  return (
    endpoint.catalog_document_id !== null &&
    endpoint.catalog_document_id !== catalogDocumentId(snapshot)
  );
}

function draftVehicleFromSnapshot(vehicle: VehicleSnapshot): ShiftEditorDraft['vehicles'][number] {
  return {
    vehicle_id: vehicle.vehicle_id,
    series: vehicle.series,
    full_number: vehicle.full_number ?? '',
    nickname: vehicle.nickname ?? '',
    operator: vehicle.operator ?? '',
    vehicle_type: vehicle.vehicle_type,
  };
}

function confirmedEndpointResult(endpoint: ShiftEditorDraftEndpoint) {
  return shiftEditorDraftConfirmedEndpointSchema.safeParse({
    location_id: endpoint.location_id,
    name: endpoint.name,
    country_code: endpoint.country_code,
    key_identifier: endpoint.key_identifier,
    catalog_document_id: endpoint.catalog_document_id,
    catalog_version: endpoint.catalog_version,
  });
}

function confirmedEndpoint(endpoint: ShiftEditorDraftEndpoint): ShiftEditorDraftConfirmedEndpoint {
  return shiftEditorDraftConfirmedEndpointSchema.parse({
    location_id: endpoint.location_id,
    name: endpoint.name,
    country_code: endpoint.country_code,
    key_identifier: endpoint.key_identifier,
    catalog_document_id: endpoint.catalog_document_id,
    catalog_version: endpoint.catalog_version,
  });
}

function sameDraftIdentifier(
  left: ShiftEditorDraftEndpoint['key_identifier'],
  right: ShiftEditorDraftEndpoint['key_identifier'],
): boolean {
  if (left === null || right === null) return left === right;
  return (
    left.namespace === right.namespace &&
    left.namespace_other === right.namespace_other &&
    left.value === right.value
  );
}

function collectLocationMutations(
  endpoints: readonly (ShiftEditorDraftEndpoint | null)[],
  metadata: Location['metadata'],
): UserDefinedLocationMutation[] {
  const mutations = new Map<string, UserDefinedLocationMutation>();
  for (const endpoint of endpoints) {
    if (
      endpoint === null ||
      endpoint.carried_location_snapshot !== null ||
      !confirmedEndpointResult(endpoint).success
    ) {
      continue;
    }
    if (endpoint.catalog_document_id !== null || endpoint.catalog_version !== null) {
      throw new Error('catalog endpoint cannot be persisted as a user-defined location');
    }
    const value = createUserDefinedLocation(endpoint, metadata);
    const duplicate = mutations.get(value.location_id);
    if (
      duplicate !== undefined &&
      (duplicate.value.official_name !== value.official_name ||
        duplicate.value.country_code !== value.country_code)
    ) {
      throw new Error('one location identity cannot describe two different endpoints');
    }
    mutations.set(value.location_id, { value, expected_revision: null });
  }
  return [...mutations.values()];
}

function createUserDefinedLocation(
  endpoint: ShiftEditorDraftEndpoint,
  metadata: Location['metadata'],
): Omit<Location, 'metadata'> {
  const finalEndpoint = confirmedEndpoint(endpoint);
  const identifier = finalEndpoint.key_identifier;
  const parsed = locationSchema.parse({
    location_id: finalEndpoint.location_id,
    official_name: finalEndpoint.name,
    short_name: null,
    country_code: finalEndpoint.country_code,
    region: null,
    municipality: null,
    location_type: 'operational_point',
    coordinate: null,
    aliases: [],
    historical_names: [],
    search_variants: [],
    operator: null,
    valid_from: null,
    valid_to: null,
    operational_status: 'unknown',
    origin: 'user_defined',
    identifiers:
      identifier === null
        ? []
        : [
            {
              location_identifier_id: identifierUuid(finalEndpoint),
              location_id: finalEndpoint.location_id,
              namespace: identifier.namespace,
              namespace_other: identifier.namespace_other,
              value: identifier.value,
              publisher: 'Uživatelský záznam',
              valid_from: null,
              valid_to: null,
              operational_status: 'unknown',
              snapshot_version: 'user-defined.v1',
              snapshot_sha256: sha256HexUtf8(
                `${identifier.namespace}\u0000${identifier.namespace_other ?? ''}\u0000${identifier.value}`,
              ),
              binding_method: 'direct',
            },
          ],
    source_claims: [],
    metadata,
  });
  return withoutRevisionMetadata(parsed);
}

function identifierUuid(endpoint: ShiftEditorDraftConfirmedEndpoint): string {
  const identifier = endpoint.key_identifier;
  if (identifier === null) throw new Error('identifier UUID requires an endpoint identifier');
  const digest = sha256HexUtf8(
    [
      'dmf.alpha.location-identifier.v1',
      endpoint.location_id,
      identifier.namespace,
      identifier.namespace_other ?? '',
      identifier.value,
    ].join('\u0000'),
  );
  const bytes = Uint8Array.from(
    Array.from({ length: 16 }, (_, index) =>
      Number.parseInt(digest.slice(index * 2, index * 2 + 2), 16),
    ),
  );
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x80;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return uuidSchema.parse(
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`,
  );
}

function catalogVersionForEndpoints(
  ...endpoints: readonly (ShiftEditorDraftEndpoint | null)[]
): ArtifactVersion | null {
  const versions = endpoints
    .map((endpoint) => endpoint?.catalog_version ?? null)
    .filter((version): version is ArtifactVersion => version !== null);
  const first = versions[0] ?? null;
  if (
    first !== null &&
    versions.some((version) => version.version !== first.version || version.sha256 !== first.sha256)
  ) {
    throw new Error('segment endpoints reference different catalog artifacts');
  }
  return first;
}

function mergeRouteCatalogVersion(
  seeded: ArtifactVersion | null,
  endpoints: ArtifactVersion | null,
): ArtifactVersion | null {
  if (
    seeded !== null &&
    endpoints !== null &&
    (seeded.version !== endpoints.version || seeded.sha256 !== endpoints.sha256)
  ) {
    throw new Error('route seed and current endpoints reference different catalog artifacts');
  }
  return seeded ?? endpoints;
}

function catalogDocumentId(snapshot: LocationSnapshot): string | null {
  return (
    snapshot.key_identifiers.find(
      (identifier) => identifier.namespace === 'OTHER' && identifier.namespace_other === 'OSM',
    )?.value ?? null
  );
}

function mergeUsageContexts(
  existing: RouteVariant['usage_contexts'],
  next: RouteVariant['usage_contexts'][number],
): RouteVariant['usage_contexts'] {
  const key = JSON.stringify(next);
  const withoutSame = existing.filter((context) => JSON.stringify(context) !== key);
  return [...withoutSame, next].slice(-512);
}

function requiredTrimmed(value: string, field: string): string {
  const trimmed = value.trim();
  if (trimmed === '') throw new Error(`${field} must not be blank`);
  return trimmed;
}

function nullableTrimmed(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function requiredVehicleType(
  value: ShiftEditorDraft['vehicles'][number]['vehicle_type'],
): NonNullable<ShiftEditorDraft['vehicles'][number]['vehicle_type']> {
  if (value === null) throw new Error('vehicle type is required');
  return value;
}

function issueField(value: PropertyKey | undefined): ShiftDraftValidationIssue['field'] {
  if (value === 'origin' || value === 'destination' || value === 'planned_destination')
    return value;
  if (value === 'vehicles') return 'vehicles';
  if (value === 'distance_metres') return 'distance';
  return 'route_confirmation';
}

function liveSegments(aggregate: ShiftAggregate): TripSegment[] {
  return aggregate.segments.filter((segment) => segment.metadata.tombstone === null);
}

function assertOpenShift(aggregate: ShiftAggregate): void {
  if (aggregate.shift.status !== 'open' || aggregate.shift.metadata.tombstone !== null) {
    throw new Error('closed shift must be explicitly reopened before editing segments');
  }
}

function nextUuid(uuid: AlphaUuidFactory): string {
  return uuidSchema.parse(uuid());
}
